import { Box, useToast } from "@chakra-ui/react";
import * as Sentry from "@sentry/react";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { extractErrorMessage } from "@equidefi/portals";
import { DocusealOverlay } from "@equidefi/portals/components/docuseal/DocusealOverlay";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useDocusealCurrentUser } from "@equidefi/portals/hooks/useDocuseal";
import { useCreateSubmission } from "@equidefi/portals/hooks/useSignatures";
import { useCurrentUser } from "@equidefi/portals/hooks/useUsers";

import { WorkflowButton } from "@/components/workflow/WorkflowButton";
import { useInvestment } from "@/hooks/useInvestments";
import { AgreementStepWrapper } from "@/pages/workflow/agreement/AgreementStepWrapper";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";

type DocuSealWorkflowStepProps = {
  onContinue?: () => void;
  agreement?: object;
  hasAdditionalSigners?: boolean;
};

export const DocuSealWorkflowStep: React.FC<DocuSealWorkflowStepProps> = ({
  agreement,
  hasAdditionalSigners = false,
  onContinue = () => {},
}) => {
  const toast = useToast();
  const [signed, setSigned] = useState(false);
  const [closed, setClosed] = useState(false);

  const { investment: CInvestment, analytics, refetchInvestment: refetch } = useWorkflowContext();

  const { data: user } = useCurrentUser();
  const { data: investment } = useInvestment(CInvestment?.id, {
    refetchInterval: closed ? 2_500 : false,
  });

  const startSubmission = useCreateSubmission({
    onSuccess: () => {
      docuseal.overlay.onOpen();
    },
  });

  const investmentAgreementId = agreement?.id;
  const hasSubmission = !!agreement?.api_response;

  const docuseal = useDocusealCurrentUser(investmentAgreementId, { enabled: hasSubmission });

  useDocumentTitle(["Subscription Agreement", investment?.offering?.name, investment?.issuer?.name]);

  const primarySigner = useMemo(
    () => investment?.signing_parties?.find((party) => party.order === 1 || party.role === "signer1"),
    [investment],
  );

  const hasSigned = signed || primarySigner?.status === "signed";

  useEffect(() => {
    if (hasSigned) {
      setClosed(false);
      onContinue();
    }
  }, [hasSigned, onContinue]);

  const handleStartSubmission = useCallback(async () => {
    analytics.track("signAgreement");

    const agreement = investment?.investment_agreements?.at(0);
    const submission = agreement?.api_response;

    if (!submission || agreement?.status !== "PENDING") {
      try {
        await startSubmission.mutateAsync({ investmentId: investment.id });
        await refetch();
      } catch (e) {
        console.error(e);
        Sentry.captureException(e, {
          user: {
            id: user?.id,
            email: user?.email,
          },
        });
        toast({
          status: "error",
          description: extractErrorMessage(e, "Sorry, something went wrong."),
        });
      }
    } else {
      docuseal.overlay.onOpen();
    }
  }, [
    analytics,
    investment?.investment_agreements,
    investment?.id,
    refetch,
    startSubmission,
    user?.id,
    user?.email,
    toast,
    docuseal.overlay,
  ]);

  return (
    <AgreementStepWrapper
      hasAdditionalSigners={hasAdditionalSigners}
      poweredBy={
        <Box fontSize="1.5rem" lineHeight="2rem" fontWeight="bold" color="black">
          DocuSeal
        </Box>
      }
      button={
        <WorkflowButton
          isLoading={startSubmission.isLoading}
          isDisabled={docuseal.overlay.isOpen && !hasSigned}
          onClick={hasSigned ? onContinue : handleStartSubmission}
          mt="4">
          {hasSigned ? "Continue" : "Start Signing"}
        </WorkflowButton>
      }>
      {docuseal.token && (
        <DocusealOverlay
          isOpen={docuseal.overlay.isOpen}
          onClose={docuseal.overlay.onClose}
          token={docuseal.token}
          src={docuseal.url}
          name={docuseal.name}
          onComplete={() => {
            setSigned(true);
            docuseal.overlay.onClose();
            toast({
              status: "success",
              description: "Subscription Agreement has been signed!",
            });
            refetch();
            onContinue();
          }}
          role={docuseal.role}
        />
      )}
    </AgreementStepWrapper>
  );
};
